import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Clipboard, RefreshCw, Save, Sparkles, Mail } from 'lucide-react';
import MarkdownRenderer from '@/components/MarkdownRenderer';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import TemplateForm from '@/components/communication/TemplateForm';
import { saveTemplate } from '@/services/communication/templates';
import CustomPromptDialog from '@/components/communication/CustomPromptDialog';
import { SendEmailDialog } from '@/components/communication/SendEmailDialog';
import { useAuth } from '@/context/AuthContext';
import { getPrimaryEmail } from '@/utils/emailUtils';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { Lead } from '@/types/lead';
import { useDefaultSender } from '@/hooks/useDefaultSender';

interface EmailTemplateViewProps {
  emailTemplate: string;
  isLoading: boolean;
  onGenerate: () => Promise<void>;
  onGenerateWithPrompt: (promptText: string) => Promise<void>;
  leadStatus?: string;
  lead?: Lead;
  emailValidation?: any;
  compact?: boolean;
}

const EmailTemplateView: React.FC<EmailTemplateViewProps> = ({
  emailTemplate,
  isLoading,
  onGenerate,
  onGenerateWithPrompt,
  leadStatus,
  lead,
  emailValidation,
  compact = false
}) => {
  const { toast } = useToast();
  const { profile } = useAuth();
  const { defaultSender } = useDefaultSender();
  const [isSaving, setIsSaving] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isCustomPromptOpen, setIsCustomPromptOpen] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Email Template Copied',
      description: "Text has been copied to your clipboard."
    });
  };

  const handleSaveTemplate = async (templateData: any) => {
    setIsSaving(true);
    try {
      const result = await saveTemplate({
        ...templateData,
        type: 'email',
      });
      
      if (result) {
        toast({
          title: "Email Template Saved",
          description: "Your template has been saved successfully."
        });
        setIsSaveDialogOpen(false);
      } else {
        throw new Error("Failed to save template");
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save email template."
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCustomGeneration = (promptText: string) => {
    setIsCustomPromptOpen(false);
    onGenerateWithPrompt(promptText);
  };

  const handleSendEmail = () => {
    if (!lead) {
      toast({
        title: 'No Lead',
        description: 'Lead information not available',
        variant: 'destructive',
      });
      return;
    }

    if (!emailTemplate) {
      toast({
        title: 'No Template',
        description: 'Please generate an email template first',
        variant: 'destructive',
      });
      return;
    }

    setShowSendDialog(true);
  };

  const getLeadEmail = () => {
    if (!lead?.email) return '';
    return getPrimaryEmail(lead.email);
  };

  const getSenderName = () => {
    if (defaultSender) {
      return defaultSender.from_name;
    }
    if (profile?.preferred_from_name) return profile.preferred_from_name;
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    return profile?.first_name || '';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-3">
        <h3 className={`font-semibold ${compact ? 'text-base' : 'text-lg'}`}>Email Template</h3>
        {emailValidation && (
          <Badge variant={emailValidation.isVerified ? 'default' : 'secondary'} className="gap-1.5">
            {emailValidation.isVerified ? (
              <CheckCircle2 className="h-3 w-3" />
            ) : (
              <AlertCircle className="h-3 w-3" />
            )}
            <span className="text-xs">{emailValidation.isVerified ? 'Ready' : 'Setup Required'}</span>
          </Badge>
        )}
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsCustomPromptOpen(true)}
          disabled={isLoading}
          className="h-9"
        >
          <Sparkles className="h-4 w-4 mr-2" />
          Customize
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onGenerate}
          disabled={isLoading}
          className="h-9"
        >
          {isLoading ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              {emailTemplate ? 'Regenerate' : 'Generate'}
            </>
          )}
        </Button>
        {emailTemplate && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(emailTemplate)}
              className="h-9"
            >
              <Clipboard className="h-4 w-4 mr-2" />
              Copy
            </Button>
            <Dialog open={isSaveDialogOpen} onOpenChange={setIsSaveDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[700px]">
                <TemplateForm
                  type="email"
                  template={{
                    content: emailTemplate,
                    leadStatus: leadStatus,
                    name: `Email template for ${leadStatus || 'lead'}`
                  }}
                  onSave={handleSaveTemplate}
                  onCancel={() => setIsSaveDialogOpen(false)}
                />
              </DialogContent>
            </Dialog>
            {lead && (
              <Button
                size="sm"
                onClick={handleSendEmail}
                className="h-9"
              >
                <Mail className="h-4 w-4 mr-2" />
                Send
              </Button>
            )}
          </>
        )}
      </div>

      {emailTemplate ? (
        <div className={`bg-muted/50 p-6 rounded-md overflow-auto ${compact ? 'max-h-[300px]' : 'max-h-[500px]'}`}>
          <MarkdownRenderer content={emailTemplate} />
        </div>
      ) : (
        <div className="bg-muted/30 p-8 rounded-md text-center">
          <p className="text-muted-foreground">
            No email template generated yet. Click Generate to create a personalized email template.
          </p>
        </div>
      )}

      <CustomPromptDialog
        isOpen={isCustomPromptOpen}
        onClose={() => setIsCustomPromptOpen(false)}
        onSubmit={handleCustomGeneration}
        type="email"
        isLoading={isLoading}
      />

      {lead && (
        <SendEmailDialog
          open={showSendDialog}
          onOpenChange={setShowSendDialog}
          leadId={lead.id}
          leadName={lead.name}
          leadEmail={getLeadEmail()}
          defaultSubject={`Follow-up: ${lead.name}`}
          defaultBody={emailTemplate}
          fromName={getSenderName()}
          fromEmail={profile?.work_email || ''}
          onEmailSent={() => {
            toast({
              title: 'Email Sent',
              description: 'Your email has been sent successfully',
            });
          }}
        />
      )}
    </div>
  );
};

export default EmailTemplateView;
